<template>
  <div :class="[$b(), 'global-wrapper']">
    <!-- Header -->
    <div :class="$b('header')">
      <div :class="[$b('header-content'), 'global-wrapper']">
        <BaseHeading
          tag="h1"
          size="h1"
          :class="$b('heading')"
        >
          {{ $tr('Customer Reviews') }}
        </BaseHeading>

        <div :class="$b('header-right')">
          <div :class="$b('header-stars')">
            <ReviewStars
              :count="globalCount"
              :rating="aggregated.averageStars"
              header-mode
            />
          </div>

          <div :class="$b('header-recommend')">
            <VisionIcon
              id="reviews-header-recommend-thumbs-up"
              icon="thumbUp"
              gradient="default"
              :class="$b('header-recommend-icon')"
            />

            <BaseHeading
              tag="h2"
              size="h2"
              :class="$b('header-recommend-heading')"
            >
              {{ $tp(aggregated.recommendRate || 0.995) }}
            </BaseHeading>

            <BaseCopy
              tag="p"
              size="body"
              :class="$b('header-recommend-text')"
            >
              {{ $tr('would recommend SunGod to a friend') }}
            </BaseCopy>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="localCount >= 10"
      :class="$b('toggle-global-reviews')"
    >
      <RadioTabs
        as-line-link
        :tabs="[
          {
            value: 'global',
            name: `All Reviews (${$tn(globalCount)})`,
          },
          {
            value: 'local',
            name: `${countryName} (${$tn(localCount)})`,
          },
        ]"
        :active-tab-index="showLocalisedReviews ? 1 : 0"
        @selected="showLocalisedReviews = $event.value === 'local'"
      />
    </div>

    <div class="reviews">
      <InfiniteReviews
        v-model:show-localised-reviews="showLocalisedReviews"
        v-model:show-reviews-toggle="showReviewsToggle"
        use-generic-reviews
        :is-on-product-page="false"
        @review-product-clicked="handleReviewClick"
      />
    </div>
  </div>
</template>

<script>
import { reviewsSummaryQuery } from '~/gql/reviews.gql'

export default defineNuxtComponent({
  name: 'ReviewsPage',

  async setup() {
    definePageMeta({
      pageType: 'Reviews Page'
    })

    const storefrontStore = useStorefrontStore()
    const { $tn, $tp, runWithContext } = useNuxtApp()
    const { storeCountry } = useStorefront()
    const { getTitle, getDescription } = useMetaHelper()

    const countryName = storefrontStore.countryNameFromCode(storeCountry.value || 'GB')

    const queryVariables = {
      country: storeCountry?.value || 'GB'
    }
    const response = await useDatoQuery({
      query: reviewsSummaryQuery,
      variables: queryVariables
    })

    if (response.error.value) {
      console.error(response.error.value)
      throw createError({
        statusCode: 500,
        statusMessage: response.error.value?.message || response.error.value,
      })
    }

    const { resolveLocalePath } = useUrlBuilder()

    runWithContext(() => useHead({
      title: getTitle('Product Reviews'),
      meta: [
        ...getDescription(
          'Rated {rating}/5 by {count} verified customers. {percentage} of customers would recommend SunGod to a friend.',
          {
            rating: $tn(response.data?.value?.aggregated?.averageStars || 4.8, 2),
            count: $tn(response.data?.value?.global?.count || 1420),
            percentage: $tp(response.data?.value?.aggregated?.recommendRate || 0.995),
          }
        ),
        {
          hid: 'og:title',
          property: 'og:title',
          content: getTitle('Product Reviews'),
        },
      ],
    }))

    return {
      storefrontStore,
      storeCountry,
      resolveLocalePath,
      countryName,
      response
    }
  },

  data: () => ({
    showLocalisedReviews: true,
    showReviewsToggle: false,
  }),

  computed: {
    toggleLocalisedReviews() {
      return this.showLocalisedReviews
    },

    globalCount() {
      return this.response.data?.value?.global?.count || 0
    },

    localCount() {
      return this.response.data?.value?.local?.count || 0
    },

    aggregated() {
      return this.response.data?.value?.aggregated || {}
    }
  },

  methods: {
    handleReviewClick(sku) {
      navigateTo(this.resolveLocalePath(`/shop/redirect?sku=${sku}`))
    },
  },
})
</script>

<style lang="postcss">
.ReviewsPage {
  @apply mb-xxl;
}

.ReviewsPage__header {
  @apply bg-grey-light
    flex items-center justify-between
    py-lg mb-md
    text-off-black text-center;
  margin-left: calc(0px - var(--outer-gutter));
  margin-right: calc(0px - var(--outer-gutter));

  @screen xxl {
    margin-left: calc(0px - (((100vw - 1500px) / 2) + var(--outer-gutter)));
    margin-right: calc(0px - (((100vw - 1500px) / 2) + var(--outer-gutter)));
  }
}

.ReviewsPage__header-content {
  @apply flex flex-col xl:flex-row items-start xl:items-center justify-between;
}

.ReviewsPage__heading {
  @apply pb-md xl:pb-0 lg:pr-xs
    mt-0
    text-left uppercase text-4xl;

  flex: 1 1 33%;
}

.ReviewsPage__header-right {
  @apply flex flex-col md:flex-row items-start justify-start md:justify-around;
  flex: 1 1 66%;

  > * {
    @apply flex;
    flex: 1 1 50%;
  }
}

.ReviewsPage__header-stars {
  @apply md:border-r md:border-off-black md:border-opacity-20;
  @screen md {
    padding-right: 10%;
  }
}

.ReviewsPage__header-recommend {
  @apply flex flex-wrap justify-start items-center
    text-left;
  @screen md {
    padding-left: 10%;
  }
}

.ReviewsPage__header-recommend-heading {
  @apply mt-0 ml-xs md:ml-sm;
}

.ReviewsPage__header-recommend-text {
  @apply mt-xxs
    w-full;
}

.ReviewsPage__header-recommend-icon {
  transform: translateY(-0.15em);
}

.ReviewsPage__toggle-global-reviews {
  @apply flex items-center justify-end mb-lg space-x-2;
}
</style>
